import React from 'react';
import { AlertCircle, AlertTriangle, Info, CheckCircle2, MapPin } from 'lucide-react';
import type { DocumentFlagInfo } from '@schemas/api';
import { formatNumber } from '@utils/formatters';

interface DocumentFlagsPanelProps {
  flags: DocumentFlagInfo[];
  /** A QST name mismatch can only rename an existing, linked vendor record. */
  canUpdateVendorName?: boolean;
  onApplyAction?: (
    flag: DocumentFlagInfo,
    actionType: string,
    payload?: Record<string, any>
  ) => Promise<void>;
  isApplyingAction?: boolean;
}

const severityConfig: Record<string, { icon: React.ElementType; border: string; bg: string; text: string; badge: string }> = {
  critical: {
    icon: AlertCircle,
    border: 'border-red-200 dark:border-red-800',
    bg: 'bg-red-50 dark:bg-red-950',
    text: 'text-red-800 dark:text-red-200',
    badge: 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300',
  },
  error: {
    icon: AlertCircle,
    border: 'border-orange-200 dark:border-orange-800',
    bg: 'bg-orange-50 dark:bg-orange-950',
    text: 'text-orange-800 dark:text-orange-200',
    badge: 'bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300',
  },
  warning: {
    icon: AlertTriangle,
    border: 'border-yellow-200 dark:border-yellow-800',
    bg: 'bg-yellow-50 dark:bg-yellow-950',
    text: 'text-yellow-800 dark:text-yellow-200',
    badge: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300',
  },
  info: {
    icon: Info,
    border: 'border-blue-200 dark:border-blue-800',
    bg: 'bg-blue-50 dark:bg-blue-950',
    text: 'text-blue-800 dark:text-blue-200',
    badge: 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300',
  },
};

const defaultSeverity = severityConfig.warning;

type FlagDetails = Record<string, unknown>;

interface MismatchComponent {
  key?: string;
  label?: string;
  value?: unknown;
}

interface MismatchComparison {
  left_label?: string;
  left_value?: unknown;
  operator?: string;
  right_label?: string;
  right_value?: unknown;
}

const hiddenDetailKeys = new Set(['vendor_id', 'vendor_uuid', 'vendor_invoice_rule_id']);

function formatFlagType(flagType: string): string {
  return flagType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function formatFlagValue(value: unknown): string {
  if (typeof value === 'number') {
    return formatNumber(value, 2);
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (value === null || value === undefined) {
    return '-';
  }
  return JSON.stringify(value);
}

function formatDetailLabel(key: string): string {
  return formatFlagType(key);
}

function displayString(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return formatFlagValue(value);
  }
  return null;
}

function parseRecord(value: unknown): Record<string, unknown> | null {
  if (isRecord(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(value);
      return isRecord(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  return null;
}

function formatAddressLines(value: unknown): string[] {
  const address = parseRecord(value);
  if (!address) {
    const fallback = displayString(value);
    return fallback ? [fallback] : [];
  }

  const line1 = displayString(address.line1);
  const line2 = displayString(address.line2);
  const city = displayString(address.city);
  const state = displayString(address.state);
  const postalCode = displayString(address.postal_code ?? address.postalCode);
  const country = displayString(address.country);
  const statePostal = [state, postalCode].filter(Boolean).join(' ');
  const locality = [city, statePostal].filter(Boolean).join(', ');

  return [line1, line2, locality, country].filter((line): line is string => Boolean(line));
}

function hasAddressShape(value: unknown): boolean {
  const address = parseRecord(value);
  return Boolean(
    address &&
      ['line1', 'line2', 'city', 'state', 'postal_code', 'postalCode', 'country'].some(
        (key) => address[key] !== undefined
      )
  );
}

function AddressSummary({
  label,
  value,
}: {
  label: string;
  value: unknown;
}): React.ReactElement {
  const lines = formatAddressLines(value);

  return (
    <div className="min-w-0 border-l border-gray-200 pl-3 dark:border-gray-700">
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        <MapPin className="h-3 w-3" />
        {label}
      </p>
      {lines.length > 0 ? (
        <address className="mt-1 space-y-0.5 not-italic leading-5 text-gray-900 dark:text-gray-100">
          {lines.map((line, index) => (
            <div key={`${label}-${index}`}>{line}</div>
          ))}
        </address>
      ) : (
        <p className="mt-1 text-gray-500 dark:text-gray-400">No address provided</p>
      )}
    </div>
  );
}

function isMismatchFlag(flagType: string): boolean {
  return flagType === 'invoice_sum_mismatch' || flagType === 'line_items_sum_mismatch';
}

function isAddressMismatchFlag(flag: DocumentFlagInfo): boolean {
  const details = flag.details;
  if (!details) return false;

  return (
    flag.flag_type.includes('address_mismatch') ||
    hasAddressShape(details.extracted_address) ||
    hasAddressShape(details.vendor_address_on_file)
  );
}

function buildLegacyMismatchComponents(
  flagType: string,
  details: FlagDetails
): MismatchComponent[] {
  if (flagType === 'line_items_sum_mismatch') {
    return [
      { key: 'line_items_total', label: 'Line items total', value: details.line_items_total },
      { key: 'discount', label: 'Discount', value: details.discount },
      { key: 'additional_charges', label: 'Additional charges', value: details.additional_charges },
      { key: 'computed_subtotal', label: 'Computed subtotal', value: details.computed_subtotal },
      { key: 'extracted_subtotal', label: 'Extracted subtotal', value: details.extracted_subtotal },
    ].filter((component) => component.value !== undefined);
  }

  return [
    { key: 'subtotal', label: 'Subtotal', value: details.subtotal },
    { key: 'tax', label: 'Tax', value: details.tax },
    { key: 'computed_total', label: 'Computed total', value: details.computed_total },
    { key: 'extracted_total', label: 'Extracted total', value: details.extracted_total },
  ].filter((component) => component.value !== undefined);
}

function getMismatchEquation(flagType: string, details: FlagDetails): string | null {
  if (typeof details.equation_with_values === 'string') {
    return details.equation_with_values;
  }

  if (flagType === 'line_items_sum_mismatch') {
    const components = buildLegacyMismatchComponents(flagType, details);
    if (components.length < 4) {
      return null;
    }
    return `${formatFlagValue(details.line_items_total)} - ${formatFlagValue(details.discount ?? 0)} + ${formatFlagValue(details.additional_charges ?? 0)} = ${formatFlagValue(details.computed_subtotal)}`;
  }

  const components = buildLegacyMismatchComponents(flagType, details);
  if (components.length < 4) {
    return null;
  }
  return `${formatFlagValue(details.subtotal)} + ${formatFlagValue(details.tax ?? 0)} = ${formatFlagValue(details.computed_total)}`;
}

function getMismatchComparison(
  flagType: string,
  details: FlagDetails
): MismatchComparison | null {
  if (isRecord(details.comparison)) {
    return {
      left_label: typeof details.comparison.left_label === 'string' ? details.comparison.left_label : undefined,
      left_value: details.comparison.left_value,
      operator: typeof details.comparison.operator === 'string' ? details.comparison.operator : undefined,
      right_label: typeof details.comparison.right_label === 'string' ? details.comparison.right_label : undefined,
      right_value: details.comparison.right_value,
    };
  }

  if (flagType === 'line_items_sum_mismatch') {
    return {
      left_label: 'Computed subtotal',
      left_value: details.computed_subtotal,
      operator: '!=',
      right_label: 'Extracted subtotal',
      right_value: details.extracted_subtotal,
    };
  }

  return {
    left_label: 'Computed total',
    left_value: details.computed_total,
    operator: '!=',
    right_label: 'Extracted total',
    right_value: details.extracted_total,
  };
}

function renderFlagDetails(flag: DocumentFlagInfo): React.ReactNode {
  const details = flag.details;
  if (!details || Object.keys(details).length === 0) {
    return null;
  }

  if (isAddressMismatchFlag(flag)) {
    const description = displayString(details.description);
    const vendorName = displayString(details.vendor_name);
    const extractedAddress = details.extracted_address ?? details.vendor_address_extracted;
    const vendorAddress = details.vendor_address_on_file ?? details.vendor_address;
    const extraDetails = Object.entries(details).filter(
      ([key]) =>
        !hiddenDetailKeys.has(key) &&
        ![
          'description',
          'vendor_name',
          'extracted_address',
          'vendor_address_extracted',
          'vendor_address_on_file',
          'vendor_address',
        ].includes(key)
    );

    return (
      <div className="mt-3 space-y-3 text-xs text-gray-700 dark:text-gray-300">
        {description && (
          <p className="leading-5 text-gray-700 dark:text-gray-300">{description}</p>
        )}
        {vendorName && (
          <div className="inline-flex max-w-full items-center gap-2 rounded-md bg-white/70 px-2.5 py-1.5 text-gray-700 shadow-sm ring-1 ring-gray-200/80 dark:bg-gray-900/40 dark:text-gray-200 dark:ring-gray-700">
            <span className="text-gray-500 dark:text-gray-400">Vendor</span>
            <span className="truncate font-medium text-gray-900 dark:text-gray-100">{vendorName}</span>
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <AddressSummary label="Extracted address" value={extractedAddress} />
          <AddressSummary label="Vendor address on file" value={vendorAddress} />
        </div>
        {extraDetails.length > 0 && (
          <div className="space-y-1 border-t border-gray-200/80 pt-2 dark:border-gray-700">
            {extraDetails.map(([key, value]) => (
              <div key={key} className="grid gap-1 sm:grid-cols-[12rem_minmax(0,1fr)]">
                <span className="font-medium text-gray-600 dark:text-gray-400">{formatDetailLabel(key)}</span>
                <span className="min-w-0 break-words text-gray-800 dark:text-gray-200">
                  {formatFlagValue(value)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (isMismatchFlag(flag.flag_type)) {
    const rawComponents = Array.isArray(details.components)
      ? details.components.filter(isRecord).map((component) => ({
          key: typeof component.key === 'string' ? component.key : undefined,
          label: typeof component.label === 'string' ? component.label : undefined,
          value: component.value,
        }))
      : [];
    const components = rawComponents.length > 0
      ? rawComponents
      : buildLegacyMismatchComponents(flag.flag_type, details);
    const equation = getMismatchEquation(flag.flag_type, details);
    const comparison = getMismatchComparison(flag.flag_type, details);

    return (
      <div className="mt-2 space-y-2 text-xs text-gray-700 dark:text-gray-300">
        {components.length > 0 && (
          <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1">
            {components.map((component, index) => (
              <React.Fragment key={component.key || `${flag.flag_type}-${index}`}>
                <span className="font-medium text-gray-800 dark:text-gray-200">
                  {component.label || formatFlagType(component.key || 'value')}
                </span>
                <span className="font-mono text-right">{formatFlagValue(component.value)}</span>
              </React.Fragment>
            ))}
          </div>
        )}
        {equation && (
          <div className="rounded-md border border-gray-200/80 bg-white/70 px-2 py-1.5 font-mono text-[11px] text-gray-700 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300">
            Equation: {equation}
          </div>
        )}
        {comparison && (
          <div className="rounded-md border border-gray-200/80 bg-white/70 px-2 py-1.5 text-[11px] text-gray-700 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300">
            Compared values: {comparison.left_label || 'Computed'} {formatFlagValue(comparison.left_value)}{' '}
            {comparison.operator === '!=' ? 'vs.' : (comparison.operator || 'vs.')} {comparison.right_label || 'Extracted'}{' '}
            {formatFlagValue(comparison.right_value)}
          </div>
        )}
      </div>
    );
  }

  const visibleDetails = Object.entries(details).filter(([key]) => !hiddenDetailKeys.has(key));
  if (visibleDetails.length === 0) {
    return null;
  }

  return (
    <div className="mt-1 text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
      {visibleDetails.map(([key, value]) => (
        <div key={key} className="grid gap-1 sm:grid-cols-[12rem_minmax(0,1fr)]">
          <span className="font-medium">{formatDetailLabel(key)}</span>
          <span className="min-w-0 break-words">
            {hasAddressShape(value) ? formatAddressLines(value).join(', ') : formatFlagValue(value)}
          </span>
        </div>
      ))}
    </div>
  );
}

export const DocumentFlagsPanel: React.FC<DocumentFlagsPanelProps> = ({
  flags,
  canUpdateVendorName = false,
  onApplyAction,
  isApplyingAction = false,
}) => {
  if (flags.length === 0) return null;

  const activeFlags = flags.filter((f) => f.status !== 'resolved');
  const resolvedFlags = flags.filter((f) => f.status === 'resolved');
  const getAction = (
    flag: DocumentFlagInfo
  ): { label: string; actionType: string; payload?: Record<string, any> } | null => {
    if (flag.flag_type === 'qst_vendor_name_mismatch' && canUpdateVendorName) {
      const targetName = flag.details?.api_registered_name;
      return {
        label: 'Update vendor name',
        actionType: 'update_vendor_name',
        payload: targetName ? { target_vendor_name: targetName } : undefined,
      };
    }
    return null;
  };

  return (
    <div className="space-y-3">
      {activeFlags.length > 0 && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Document Flags</h3>
          <div className="space-y-2">
            {activeFlags.map((flag, idx) => {
              const config = severityConfig[flag.severity] || defaultSeverity;
              const Icon = config.icon;
              return (
                <div
                  key={flag.id || idx}
                  className={`flex items-start gap-3 rounded-lg border ${config.border} ${config.bg} p-3`}
                >
                  <Icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${config.text}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-medium ${config.text}`}>
                        {formatFlagType(flag.flag_type)}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${config.badge}`}>
                        {flag.severity}
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 font-medium">
                        {flag.status.replace(/_/g, ' ')}
                      </span>
                    </div>
                    {renderFlagDetails(flag)}
                    {flag.flag_type === 'qst_vendor_name_mismatch' && !canUpdateVendorName && (
                      <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                        Link or create the vendor in Missing References before applying the registered name.
                      </p>
                    )}
                    {onApplyAction && getAction(flag) && (
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={() => {
                            const action = getAction(flag);
                            if (!action) return;
                            void onApplyAction(flag, action.actionType, action.payload);
                          }}
                          disabled={isApplyingAction}
                          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isApplyingAction ? 'Applying...' : getAction(flag)?.label}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {resolvedFlags.length > 0 && (
        <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950 p-4">
          <h3 className="font-semibold text-green-900 dark:text-green-100 mb-2">Resolved Flags</h3>
          <div className="space-y-1">
            {resolvedFlags.map((flag, idx) => (
              <div key={flag.id || idx} className="flex items-center gap-2 text-sm text-green-800 dark:text-green-200">
                <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                <span>{formatFlagType(flag.flag_type)}</span>
                <span className="text-xs text-green-600 dark:text-green-400">({flag.severity})</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
